import React, { memo, useCallback, useContext } from 'react';
import { Avatar, Card, Col, Dropdown, Layout, Row, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MenuInfo } from 'rc-menu/lib/interface';
import {
  ApiOutlined,
  ClockCircleOutlined,
  DownOutlined,
  GiftOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useImmer } from 'use-immer';
import { currentUserInitialState, ICurrentUser, setCurrentUser } from '@/graphql/operations/user';
import { Loader } from '@/components';
import BaseLayoutContext, { ILayoutContext } from '@/context/LegacyLayoutContext';
import classNames from 'classnames';
import HeaderSearch from '@/layouts/LegacyLayout/HeaderSearch';
import LocalStorage from '@/service/LocalStorage';
import styles from './Header.module.pcss';
import { useApolloClient } from '@apollo/client';

const GlobalRewardPreferences = React.lazy(() => import('@/pages/OrganizationPreferences/Rewards'));
const InviteAdministratorsModal = React.lazy(() => import('@/pages/OrganizationPreferences/InviteAdministrators'));
const AccountDataModal = React.lazy(() => import('@/pages/OrganizationPreferences/AccountData'));
const UICustomizationModal = React.lazy(() => import('@/pages/OrganizationPreferences/UICustomization'));

type Props = {
  isCollapsed: boolean;
  toggleSideBar: () => void;
  currentUser: ICurrentUser;
};

const { Header } = Layout;

const HeaderLayout = memo<Props>(({ toggleSideBar, isCollapsed, currentUser }) => {
  const layoutContext = useContext<ILayoutContext>(BaseLayoutContext);
  const client = useApolloClient();

  const navigate = useNavigate();
  const [state, setState] = useImmer({
    rewardsModalOpen: false,
    inviteAdministratorsModalOpen: false,
    dropDownVisible: false,
    accountDataModalOpen: false,
    UICustomizationModalOpen: false,
  });

  const onUserMenuClick = async (e: MenuInfo) => {
    if (e.key === 'logout') {
      try {
        await client.clearStore();
        setCurrentUser(currentUserInitialState);
        LocalStorage.clear();
        navigate('/signin', { replace: true });
      } catch (error) {
        return error;
      }
    }
    if (e.key === 'rewards') {
      onOpenRewardsSettingModal(true);
    }

    if (e.key === 'invite-administrators') {
      onOpenInviteAdministratorsModal(true);
    }
    if (e.key === 'account') {
      onOpenAccountSettingModal(true);
    }

    if (e.key === 'timeKeeping') {
      navigate('/preferences/widgets/timekeeping', { replace: true });
    }

    if (e.key === 'customization') {
      onOpenUICustomizationModal(true);
    }
  };

  const onOpenRewardsSettingModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.rewardsModalOpen = isOpen;
      });
    },
    [setState]
  );

  const onOpenAccountSettingModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.accountDataModalOpen = isOpen;
      });
    },
    [setState]
  );

  const onOpenInviteAdministratorsModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.inviteAdministratorsModalOpen = isOpen;
      });
    },
    [setState]
  );

  const onOpenUICustomizationModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.UICustomizationModalOpen = isOpen;
      });
    },
    [setState]
  );

  const handleVisibleChange = (visible: boolean) => {
    setState((draft) => {
      draft.dropDownVisible = visible;
    });
  };

  const defineToggleIcon = () => {
    if (isCollapsed) {
      return MenuUnfoldOutlined;
    }
    return MenuFoldOutlined;
  };

  const toggleIcon = React.createElement(defineToggleIcon(), {
    className: classNames(styles.collapseTrigger, styles[layoutContext.layoutConfig?.title.name]),
    onClick: toggleSideBar,
  });

  return (
    <>
      <Header className={classNames(styles.header, styles[layoutContext.layoutConfig?.title.name])}>
        <Row justify="space-between">
          <Col>{toggleIcon}</Col>
          <Col className={styles.right}>
            <Space size={16}>
              <HeaderSearch />
              <Dropdown
                trigger={['hover']}
                menu={{
                  items: [
                    {
                      key: 'rewards',
                      label: 'Rewards Settings',
                      icon: <GiftOutlined />,
                      className: styles.menuItem,
                    },
                    {
                      key: 'widgets',
                      label: 'Widgets',
                      children: [
                        {
                          key: 'timeKeeping',
                          label: 'Time Keeping',
                          icon: <ClockCircleOutlined />,
                        },
                      ],
                      icon: <ApiOutlined />,
                    },
                    {
                      key: 'invite-administrators',
                      label: 'Invite Administrators',
                      icon: <UsergroupAddOutlined />,
                      className: styles.menuItem,
                    },
                    {
                      key: 'customization',
                      label: 'Customization',
                      icon: <SettingOutlined />,
                      className: styles.menuItem,
                    },
                    {
                      key: 'logout',
                      label: 'Logout',
                      icon: <LogoutOutlined />,
                    },
                  ],
                  onClick: onUserMenuClick,
                  className: styles.menu,
                }}
                dropdownRender={(menu) => (
                  <>
                    <Card bordered={false} styles={{ body: { borderRadius: '0' } }}>
                      <Card.Meta description={currentUser?.email} />
                    </Card>
                    {menu}
                  </>
                )}
                onOpenChange={handleVisibleChange}
                open={state.dropDownVisible}
              >
                <div className={styles.action}>
                  <Avatar
                    size="small"
                    icon={<UserOutlined />}
                    className={classNames(styles.avatar, styles[layoutContext.layoutConfig?.title.name])}
                    alt="avatar"
                  />
                  <span>{currentUser?.name || 'User'} &nbsp;</span>
                  <DownOutlined />
                </div>
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </Header>
      {state.rewardsModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <GlobalRewardPreferences isModalVisible={state.rewardsModalOpen} toggleModal={onOpenRewardsSettingModal} />
        </React.Suspense>
      ) : null}
      {state.inviteAdministratorsModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <InviteAdministratorsModal
            isModalVisible={state.inviteAdministratorsModalOpen}
            toggleModal={onOpenInviteAdministratorsModal}
          />
        </React.Suspense>
      ) : null}
      {state.accountDataModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <AccountDataModal isModalVisible={state.accountDataModalOpen} toggleModal={onOpenAccountSettingModal} />
        </React.Suspense>
      ) : null}
      {state.UICustomizationModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <UICustomizationModal isOpen={state.UICustomizationModalOpen} toggleModal={onOpenUICustomizationModal} />
        </React.Suspense>
      ) : null}
    </>
  );
});

export default HeaderLayout;
